import React from 'react';
import { Copy, ExternalLink, Trash2, Edit2, Archive, MoreVertical } from 'lucide-react';
import { useCheckoutStore } from '../store/checkoutStore';
import { useProductStore } from '../store/productStore';
import { useNavigate } from 'react-router-dom';

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  pageName: string;
}

const DeleteModal = ({ isOpen, onClose, onConfirm, pageName }: DeleteModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Checkout Page</h3>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete "{pageName}"? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="btn bg-red-600 hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export const CheckoutLinks = () => {
  const { publishedPages, updatePublishedPage, archivePage, deletePage } = useCheckoutStore();
  const getProduct = useProductStore((state) => state.getProduct);
  const navigate = useNavigate();
  const [copied, setCopied] = React.useState<string | null>(null);
  const [openMenu, setOpenMenu] = React.useState<string | null>(null);
  const [deleteModal, setDeleteModal] = React.useState<{
    isOpen: boolean;
    pageId: string;
    pageName: string;
  }>({
    isOpen: false,
    pageId: '',
    pageName: '',
  });

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleEdit = (pageId: string) => {
    navigate(`/checkout?edit=${pageId}`);
    setOpenMenu(null);
  };

  const toggleStatus = (pageId: string, isActive: boolean) => {
    updatePublishedPage(pageId, { active: isActive });
    setOpenMenu(null);
  };

  const handleArchive = (pageId: string) => {
    archivePage(pageId);
    setOpenMenu(null);
  };

  const handleDelete = (pageId: string, pageName: string) => {
    setDeleteModal({
      isOpen: true,
      pageId,
      pageName,
    });
    setOpenMenu(null);
  };

  const confirmDelete = () => {
    if (deleteModal.pageId) {
      deletePage(deleteModal.pageId);
      setDeleteModal({
        isOpen: false,
        pageId: '',
        pageName: '',
      });
    }
  };

  const getStatusBadge = (page: any) => {
    if (page.archived) {
      return (
        <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
          Archived
        </span>
      );
    }
    return page.active ? (
      <span className="px-3 py-1 bg-green-50 text-green-700 text-sm font-medium rounded-full">
        Active
      </span>
    ) : (
      <span className="px-3 py-1 bg-yellow-50 text-yellow-700 text-sm font-medium rounded-full">
        Inactive
      </span>
    );
  };

  const getCheckoutUrl = (page: any) => {
    return `${window.location.origin}/checkout/${page.slug || page.id}`;
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Checkout Links</h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm">
        {publishedPages.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">No checkout pages published yet.</p>
          </div>
        ) : (
          <div className="divide-y">
            {publishedPages.map((page) => {
              const product = page.productId ? getProduct(page.productId) : null;
              const checkoutUrl = getCheckoutUrl(page);

              return (
                <div key={page.id} className="p-6 flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">{product?.name || 'Untitled'}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Created on {new Date(page.createdAt || Date.now()).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={checkoutUrl}
                        readOnly
                        className="w-64 input-field bg-gray-50 text-sm"
                      />
                      <button
                        onClick={() => handleCopy(checkoutUrl, page.id)}
                        className="p-2 text-gray-400 hover:text-gray-600"
                      >
                        {copied === page.id ? (
                          <span className="text-green-600">Copied!</span>
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </button>
                      <a
                        href={checkoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-gray-600"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </a>
                    </div>

                    <div className="h-8 w-[1px] bg-gray-200"></div>

                    <div className="flex items-center gap-2">
                      {getStatusBadge(page)}
                      
                      <div className="relative">
                        <button
                          onClick={() => setOpenMenu(openMenu === page.id ? null : page.id)}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50"
                        >
                          <MoreVertical className="w-5 h-5" />
                        </button>

                        {openMenu === page.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-10">
                            <button
                              onClick={() => handleEdit(page.id)}
                              className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Edit2 className="w-4 h-4" />
                              Edit Page
                            </button>
                            {!page.archived && (
                              <>
                                <button
                                  onClick={() => toggleStatus(page.id, !page.active)}
                                  className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <span className="w-4 h-4 rounded-full bg-gray-200" />
                                  Mark as {page.active ? 'Inactive' : 'Active'}
                                </button>
                                <button
                                  onClick={() => handleArchive(page.id)}
                                  className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <Archive className="w-4 h-4" />
                                  Archive Page
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleDelete(page.id, product?.name || 'Untitled')}
                              className="w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete Page
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, pageId: '', pageName: '' })}
        onConfirm={confirmDelete}
        pageName={deleteModal.pageName}
      />
    </div>
  );
};